import { useEffect, useRef, type ReactNode } from 'react';
import { S, t, type Leaf, type Locale } from '../../i18n/strings';
import styles from './xp.module.css';

interface Props {
  readonly title: Leaf;
  readonly body: Leaf;
  readonly lang: Locale;
  readonly onClose: () => void;
}

/**
 * La boîte de dialogue XP — c'est ici que vivent les blagues du skin.
 *
 * Toutes les commandes de la chrome qui ne peuvent qu'échouer (désinstaller
 * Steve, le copier, arrêter le poisson) répondent par une de ces boîtes. Cela
 * vaut mieux qu'un menu qui ne fait rien : le système a l'air vivant, et la
 * réponse reste dans le registre du site.
 *
 * Le focus part sur OK et `Échap` referme : une modale qu'on ne peut pas fermer
 * au clavier est un piège, même pour une blague.
 */
export function XpDialog({ title, body, lang, onClose }: Props): ReactNode {
  const okRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    okRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className={styles.dialogBackdrop}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t(title, lang)}
      >
        <div className={styles.titleBar}>
          <span className={styles.titleText}>{t(title, lang)}</span>
          <div className={styles.titleButtons}>
            <button
              type="button"
              className={`${styles.titleButton} ${styles.titleClose}`}
              aria-label={t(S.xp.chrome.close, lang)}
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <div className={styles.dialogBody}>
          <p className={styles.dialogText}>
            <span className={styles.dialogIcon} aria-hidden="true">
              i
            </span>
            <span>{t(body, lang)}</span>
          </p>
          <div className={styles.dialogButtons}>
            <button ref={okRef} type="button" className={styles.dialogButton} onClick={onClose}>
              {t(S.xp.dialogs.ok, lang)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
